import { useReducer, useEffect } from 'react'
import gql from 'graphql-tag'
// import moment from 'moment'
import { DateUtil, TIME } from '@utils'
import { compact } from 'lodash'
import { ApolloError } from 'apollo-boost'
import client from '@stores/apollo'

const query = gql`
  query getEventsPerCity($cities: [String], $startDate: String, $endDate: String) {
    viewer {
      company {
        events(cities: $cities, startDate: $startDate, endDate: $endDate) {
          eventType
          date
          data {
            ... on AssignmentPlan {
              id
              moveOrders {
                counters {
                  nonCanceled
                }
              }
            }
            ... on Contract {
              id
              title
              endDate
              noticeDate
              floor {
                name
                building {
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`

interface IAssignmentPlan {
  id: string
  moveOrders: {
    counters: {
      nonCanceled: number
    }
  }
}

interface IContract {
  id: string
  endDate: string
  noticeDate: string
  title: string
  floor: {
    name: string
    building: {
      name: string
    }
  }
}

export enum IEventType {
  NOTIFICATION = 'LeaseNotification',
  EXPIRATION = 'LeaseExpiration',
  MOVE = 'MovePlan',
}

type Event<T, D> = {
  eventType: T
  date: string
  data: D
}

type IAction<T, D> = {
  type: T
  payload?: D
}

type QueryAction = IAction<Actions.QUERY, undefined>
type LoadAction = IAction<Actions.LOAD, CalendarEvent[]>
type ErrorAction = IAction<Actions.ERROR, ApolloError>
type ReducerAction = QueryAction | LoadAction | ErrorAction

export type NotificationEvent = Event<IEventType.NOTIFICATION, IContract>
export type ExpirationEvent = Event<IEventType.EXPIRATION, IContract>
export type MoveEvent = Event<IEventType.MOVE, IAssignmentPlan>
export type CalendarEvent = NotificationEvent | ExpirationEvent | MoveEvent

interface IPayload {
  data: {
    viewer: {
      company: {
        events: CalendarEvent[]
      }
    }
  }
}

enum Actions {
  LOAD,
  QUERY,
  ERROR,
}

interface IState {
  isLoading: boolean
  events: CalendarEvent[] | undefined
  error: ApolloError | undefined
}

function reducer(state: IState, action: ReducerAction) {
  switch (action.type) {
    case Actions.QUERY:
      return { ...state, isLoading: true }
    case Actions.LOAD:
      return { ...state, isLoading: false, events: action.payload }
    case Actions.ERROR:
      return { ...state, error: action.payload }
    default:
      return state
  }
}

export default function useCalendarEventsByMonth({
  city,
  month,
}: {
  city: string | null

  month: Date
}) {
  const [data, dispatch] = useReducer(reducer, {
    isLoading: true,
    events: [],
    error: undefined,
  })

  useEffect(() => {
    dispatch({ type: Actions.QUERY })
    client.gql
      .query({
        query,
        variables: {
          cities: compact([city]),
          startDate: DateUtil(month).format('yyyy-mm-01'),
          endDate: DateUtil(month).endOf(TIME.MONTHS).format('yyyy-mm-dd'),
        },
      })
      .then(
        ({
          data: {
            viewer: {
              company: { events },
            },
          },
        }: IPayload) => dispatch({ type: Actions.LOAD, payload: events })
      )
      .catch((error: ApolloError) => dispatch({ type: Actions.ERROR, payload: error }))
  }, [city, month])

  return data
}
